// src/pages/firm/FirmDialog.tsx
import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";

import { Save, Plus, X } from "lucide-react";
import { SubscriptionType } from "../../types";
import { DatePicker } from "../../components/DatePicker";

import { Firm } from "../../types/firm";
import { IconTextButton } from "../../components/ButtonComponents";
import { IntegrationApplication } from "../../types/integration"; // FirmList'ten gelen tip
import { InputSwitch } from "primereact/inputswitch";

interface FirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  firm: Firm | null;
  onSave: (data: any) => void; // DTO object
  integrationApplications: IntegrationApplication[]; // Entegrasyon uygulamaları listesi
}

const subscriptionOptions = [
  { label: "Aylık", value: "monthly" },
  { label: "Yıllık", value: "yearly" },
  { label: "Ücretsiz", value: "free" },
];
const getTodayStr = () => new Date().toISOString().split('T')[0];

// Helper fonksiyon: 1 Yıl sonrasını YYYY-MM-DD formatında verir
const getNextYearStr = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
};
// DTO’ya uygun initial form
const initialData = {
  name: "",
  active: true,
  subscriptionType: "monthly",
  subscriptionAmount: 0,
  app: "",
  contractStart: getTodayStr(), // Varsayılan: Bugün
  contractEnd: getNextYearStr(), // Varsayılan: 1 Yıl Sonra
  totalCapacity: 3,
  integrationAppId: null as number | null, 
};

export const FirmDialog: React.FC<FirmDialogProps> = ({
  isOpen,
  onClose,
  firm,
  onSave,
  integrationApplications, // Destructure içine alındı
}) => {
  const [formData, setFormData] = useState(initialData);

  // dialog açıldığında form doldur
  useEffect(() => {
    if (!isOpen) return;

    if (firm) {
      setFormData({
        name: firm.name,
        active: firm.active,
        subscriptionType: firm.subscriptionType ?? "monthly",
        subscriptionAmount: firm.subscriptionAmount ?? 0,
        app: firm.app ?? "",
        contractStart: firm.contractStart
          ? firm.contractStart.split("T")[0]
          : "",
        contractEnd: firm.contractEnd
          ? firm.contractEnd.split("T")[0]
          : "",
        totalCapacity: firm.totalCapacity ?? 3,
        // integrationAppId'yi number olarak kaydet (Prisma uyumlu)
        integrationAppId: firm.integrationAppId ?? null, 
      });
    } else {
      setFormData(initialData);
    }
  }, [firm, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Zorunlu alanları kontrol et
    // integrationAppId'nin seçili olması zorunludur.
    if (!formData.name || !formData.integrationAppId) { 
      console.error("Firma Adı ve Entegrasyon Uygulaması boş bırakılamaz.");
      return;
    }

    // Backend DTO formatına dönüştür
    const payload: any = {
      ...formData,
      // number olarak tanımlandığı için null kontrolü yeterli
      integrationAppId: formData.integrationAppId, 
    };

    if (formData.contractStart) {
      payload.contractStart = new Date(formData.contractStart).toISOString();
    } else {
      delete payload.contractStart;
    }

    if (formData.contractEnd) {
      payload.contractEnd = new Date(formData.contractEnd).toISOString();
    } else {
      delete payload.contractEnd;
    }

    onSave(payload);
    onClose();
  };

  return (
    <Dialog
      header={firm ? "Firma Düzenle" : "Yeni Firma"}
      visible={isOpen}
      onHide={onClose}
      modal
        maximizable
      resizable={false}
      className="w-full md:w-8/12"
      footer={
        <div className="flex justify-end gap-3">
          <IconTextButton
            onClick={onClose}
            icon={X}
            className="btn-cancel"
          >
            İptal
          </IconTextButton>

          <IconTextButton
            onClick={handleSubmit}
            icon={firm ? Save : Plus}
            className="btn-new"
          >
            {firm ? "Güncelle" : "Firma Ekle"}
          </IconTextButton>
        </div>
      }
    >
      <div className="space-y-6 p-2">
        
        {/* === 1. Temel Bilgiler ve Entegrasyon === */}
        <div className="grid grid-cols-1 gap-4">
          
          {/* Sütun 1: Firma Adı */}
          <div className="flex flex-col gap-2">
            <label>Firma Adı *</label>
            <InputText
              value={formData.name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="Örn: ABC Yazılım A.Ş"
            />
          </div>
          
        
        </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Sütun 1: Firma Adı */}
    
          
          {/* Sütun 2: Entegrasyon Uygulaması (Dropdown) - KRİTİK ALAN */}
          <div className="flex flex-col gap-2">
            <label>Entegrasyon Uygulaması *</label>
            <Dropdown
              value={formData.integrationAppId}
              options={integrationApplications}
              optionLabel="name" // Dropdown'da görünecek alan
              optionValue="id" // Modelde tutulacak değer
              onChange={(e) =>
                setFormData((p) => ({ ...p, integrationAppId: e.value }))
              }
              placeholder="Seçiniz..."
              className="w-full"
            />
          </div>

          {/* Sütun 3: Uygulama Bilgi Notu (Eski "app" alanı) */}
          <div className="flex flex-col gap-2">
            <label>Uygulama Bilgi Notu</label>
            <InputText
              value={formData.app}
              onChange={(e) =>
                setFormData((p) => ({ ...p, app: e.target.value }))
              }
              placeholder="ERP, CRM..."
            />
          </div>
        </div>
        {/* === 2. Abonelik ve Kapasite === */}
        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
          
          {/* Abonelik Türü */}
          <div className="flex flex-col gap-2">
            <label>Abonelik Türü</label>
            <Dropdown
              value={formData.subscriptionType}
              options={subscriptionOptions}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  subscriptionType: e.value as SubscriptionType,
                }))
              }
              className="w-full"
            />
          </div>

          {/* Abonelik Miktarı */}
          <div className="flex flex-col gap-2">
            <label>Abonelik (₺)</label>
            <InputText
              type="number"
              value={formData.subscriptionAmount !== null ? String(formData.subscriptionAmount) : ""}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  subscriptionAmount: Number(e.target.value),
                }))
              }
            />
          </div>
          
          {/* Toplam Kapasite */}
          <div className="flex flex-col gap-2">
            <label>Toplam Kapasite (GB)</label>
            <InputText
              type="number"
              value={formData.totalCapacity !== null ? String(formData.totalCapacity) : ""}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  totalCapacity: Number(e.target.value),
                }))
              }
            />
          </div>

          {/* Aktif Firma Checkbox */}
          <div className="flex items-end pb-1 gap-2">
            <InputSwitch
              inputId="active"
              checked={formData.active}
              onChange={(e) =>
                setFormData((p) => ({ ...p, active: e.checked! }))
              }
            />
            <label htmlFor="active" className="cursor-pointer">
              Aktif Firma
            </label>
          </div>
        </div>

        {/* === 3. Tarih Seçimleri === */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DatePicker
            label="Sözleşme Başlangıcı"
            name="contractStart"
            value={formData.contractStart}
            onChange={(val) =>
              setFormData((p) => ({ ...p, contractStart: val }))
            }
          />

          <DatePicker
            label="Sözleşme Bitişi"
            name="contractEnd"
            value={formData.contractEnd}
            onChange={(val) =>
              setFormData((p) => ({ ...p, contractEnd: val }))
            }
          />
        </div>
      </div>
    </Dialog>
  );
};